import { useEffect, useState } from "react";
import { css, styled } from "styled-components";

import { createScrollbarStyle, mediaStyles } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { DimensionsListPanel } from "domains/createMapDetail/components/keyIssueList/DimensionListPanel";
import { KeyIssuesSection } from "domains/createMapDetail/components/keyIssueList/KeyIssuesSection";
import { getCurrentMap } from "domains/createMapDetail/selectors";
import { AddCustomContentPanel } from "domains/customContentAndSources/components/AddCustomContentPanel";
import { CreateCustomContentSection } from "domains/customContentAndSources/components/CreateCustomContentSection";
import { toggleCustomKeyIssueForm } from "domains/customKeyIssue/actions";
import { AddCustomKeyIssuePanel } from "domains/customKeyIssue/components/AddCustomKeyIssuePanel";
import { getCustomKeyIssueIsActiveForm } from "domains/customKeyIssue/selectors";
import { pushModalScreen } from "domains/modal/actions";
import { getCreateMapCustomKeyIssueAccess } from "entities/permissions/selectors";

const StyledContainer = styled.div<{ withBackground: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: scroll;
  width: 100%;

  ${createScrollbarStyle()};
  // height of a top bar
  height: calc(100% - 80px);
  ${mediaStyles(
    ({ spacing }) => `
    margin: ${spacing}rem 0 0 ${spacing}rem;
  `,
  )};
  ${({ withBackground }) =>
    withBackground &&
    css`
      background-color: ${({ theme }) => theme.colors.backgroundSecondary};
      padding: 1rem;
      border-radius: 4px;
    `}
`;

const SectionContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 1rem;

  ${createScrollbarStyle()};
`;

type KeyIssuePanelProps = { isNewMap: boolean };

enum Panel {
  DimensionList,
  AddingContent,
  CreatingKeyIssue,
}

export const KeyIssuePanel = ({ isNewMap }: KeyIssuePanelProps) => {
  const dispatch = useDispatch();
  const currentMap = useSelector(getCurrentMap);
  const isAddingKeyIssue = useSelector(getCustomKeyIssueIsActiveForm);
  const [activePanel, setActivePanel] = useState<Panel>();
  const canCreateCustomKeyIssue = useSelector(getCreateMapCustomKeyIssueAccess);

  const toggleAddingKeyIssue = () => {
    if (canCreateCustomKeyIssue) {
      dispatch(toggleCustomKeyIssueForm({ id: undefined }));
    } else {
      dispatch(pushModalScreen({ id: "CreateCustomDimensionUpgrade" }));
    }
  };

  useEffect(() => {
    setActivePanel(isAddingKeyIssue ? Panel.CreatingKeyIssue : undefined);
  }, [isAddingKeyIssue]);

  const onSetPanel = (panel: Panel) => () => setActivePanel(panel);

  if (activePanel === Panel.CreatingKeyIssue) {
    return (
      <StyledContainer withBackground>
        <AddCustomKeyIssuePanel toggleVisibility={toggleAddingKeyIssue} />
      </StyledContainer>
    );
  }
  if (activePanel === Panel.DimensionList || isNewMap) {
    return (
      <StyledContainer withBackground={false}>
        <DimensionsListPanel
          toggleAddingKeyIssue={toggleAddingKeyIssue}
          toggleVisibility={isNewMap ? undefined : () => setActivePanel(undefined)}
        />
      </StyledContainer>
    );
  }
  if (activePanel === Panel.AddingContent) {
    return (
      <StyledContainer withBackground={false}>
        <AddCustomContentPanel toggleVisibility={() => setActivePanel(undefined)} />
      </StyledContainer>
    );
  }

  // Show sections since nothing is expanded
  return (
    <StyledContainer withBackground={false}>
      <SectionContainer>
        <KeyIssuesSection
          onAddRemoveSelect={onSetPanel(Panel.DimensionList)}
          onEditKeyIssueSelect={onSetPanel(Panel.CreatingKeyIssue)}
        />
        {!isNewMap && !!currentMap?.id && (
          <CreateCustomContentSection topicId={currentMap.id} onAddContentSelect={onSetPanel(Panel.AddingContent)} />
        )}
      </SectionContainer>
    </StyledContainer>
  );
};
